import type { InterneGeldstroomRefDTO } from './interne-geldstroom'
import { z } from 'zod'
// batch-60 facturen-verwijderen (FO-443) - verwijderbaarheid + verwijder-record in de DTO's.
import type { FactuurVerwijderbaarheidDTO, FactuurVerwijderingDTO } from './facturen-verwijderen'
import type { FacturatieViaKey } from './facturatie-methodes'
// batch-63 facturen-bronregel (FO-470) — nummer-stand + verzoek-herkomst op de DTO's.
import type { FactuurNummerStand, FinqleVerzoekBronKey } from './facturen-bronregel'
// FO-419 (batch-55) — intercompany-velden op de factuur-DTO's.
import type { IntercompanyFactuurBron, IntercompanyPatroonStatus } from './intercompany-facturen'
import {
  FACTURABEL_TOELICHTING_MAX,
  FACTURABEL_TOELICHTING_MIN,
  WERKBON_NIET_FACTURABEL_REDEN_HANDMATIG,
  type WerkbonNietFacturabelReden,
} from './werkbon-facturabel-reden'
import type {
  InvoiceStatus,
  InvoiceTransitionAction,
  InvoiceLineCategory,
  WerkbonType,
  PackageType,
  ShiftType,
  InvoiceOrigin,
  CreditType,
} from './werkbonnen'

// Re-export PR-K enums + labels zodat consumenten alles via @streep/types kunnen vinden.
export type { InvoiceOrigin, CreditType } from './werkbonnen'
export { INVOICE_ORIGIN_LABELS, CREDIT_TYPE_LABELS } from './werkbonnen'

// ─── Badge varianten voor UI ──────────────────────────────────────────────────

export const INVOICE_STATUS_BADGE: Record<
  InvoiceStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  CONCEPT:         'secondary',
  GEREED:          'warning',
  VERSTUURD_KLANT: 'info',
  AKKOORD_KLANT:   'success',
  AFGEKEURD_KLANT: 'danger',
  DEFINITIEF:      'info',
  VERZONDEN:       'info',
  BETAALD:         'success',
  // Drie-routes facturatie (stap 1) — Finqle-flow statussen.
  VERZONDEN_NAAR_FINQLE: 'info',
  IN_BEHANDELING_FINQLE: 'info',
  GEFACTUREERD:          'success',
}

export const INVOICE_STATUSES: readonly InvoiceStatus[] = [
  'CONCEPT',
  'GEREED',
  'VERSTUURD_KLANT',
  'AKKOORD_KLANT',
  'AFGEKEURD_KLANT',
  'DEFINITIEF',
  'VERZONDEN',
  'BETAALD',
  // Drie-routes facturatie (stap 1) — Finqle-flow statussen.
  'VERZONDEN_NAAR_FINQLE',
  'IN_BEHANDELING_FINQLE',
  'GEFACTUREERD',
] as const

export const INVOICE_LINE_CATEGORY_LABELS: Record<InvoiceLineCategory, string> = {
  ARBEID:              'Arbeid',
  OVERUREN:            'Overuren',
  REISKOSTEN:          'Reiskosten',
  REISTIJD:            'Reistijd',
  MATERIAAL:           'Materiaal',
  PLOEGENTOESLAG:      'Ploegentoeslag',
  // PR-4 (spec 02 §2.5 + §5.5) — consignatie facturatie-categorieën.
  CONSIGNATIE_STANDBY: 'Consignatie - standby',
  CONSIGNATIE_OPROEP:  'Consignatie - oproep',
  VAST_BEDRAG:         'Vast bedrag',
  // PR-K (spec 09 §2.4) — handmatige + credit-categorieën
  ADVIES:              'Advies',
  TOESLAG:             'Toeslag',
  KORTING:             'Korting',
  OVERIG:              'Overig',
  CREDIT:              'Creditregel',
  // batch-68 controle (FO-343 §4.3/§4.4).
  MINIMUMAFNAME:       'Minimumafname',
  MEERWERK:            'Meerwerk',
}

export const INVOICE_TRANSITION_LABELS: Record<InvoiceTransitionAction, string> = {
  'maak-gereed':         'Klaarzetten',
  'terug-naar-concept':  'Terug naar concept',
  'verstuur-naar-klant': 'Verstuur naar klant',
  'klant-akkoord':       'Klant akkoord',
  'klant-afgekeurd':     'Klant afgekeurd',
  'heropen':             'Heropen',
  'maak-definitief':     'Maak definitief',
  'markeer-verzonden':   'Markeer verzonden',
  'markeer-betaald':     'Markeer als betaald',
  // Batch-3 D — correctie wanneer iemand zich vergist (geen bankkoppeling).
  'markeer-onbetaald':   'Markeer als onbetaald',
  // Batch-10 fa.1 — handmatig op gefactureerd (alleen de eigenaar/Kasper).
  'markeer-gefactureerd': 'Markeer als gefactureerd',
}

// Welke statusovergangen mag de UI aanbieden vanuit welke status?
export const INVOICE_TRANSITIONS_BY_STATUS: Record<InvoiceStatus, InvoiceTransitionAction[]> = {
  CONCEPT:         ['maak-gereed'],
  GEREED:          ['verstuur-naar-klant', 'terug-naar-concept'],
  VERSTUURD_KLANT: ['klant-akkoord', 'klant-afgekeurd'],
  AKKOORD_KLANT:   ['maak-definitief'],
  AFGEKEURD_KLANT: ['heropen'],
  // Batch-3 D — "betaald" is handmatig (geen bankkoppeling) en mag vanuit elke
  // fiscaal-uitgegeven status. DEFINITIEF houdt ook 'markeer-verzonden' (bv.
  // EIGEN-route: eerst verzonden, dan betaald), maar betaald mag al direct.
  DEFINITIEF:      ['markeer-verzonden', 'markeer-betaald'],
  VERZONDEN:       ['markeer-betaald'],
  // BETAALD is terugdraaibaar (correctie bij menselijke fout).
  BETAALD:         ['markeer-onbetaald'],
  // Drie-routes facturatie (stap 1) — Finqle-flow statussen. GEFACTUREERD
  // (Finqle/eigen-route) mag ook handmatig op betaald gezet worden; de overige
  // Finqle-tussenstatussen lopen via de Finqle-route (latere taken).
  VERZONDEN_NAAR_FINQLE: [],
  IN_BEHANDELING_FINQLE: [],
  GEFACTUREERD:          ['markeer-betaald'],
}

// ─── List & meta ──────────────────────────────────────────────────────────────

export interface InvoiceListRowDTO {
  id: string
  invoiceNumber: string
  status: InvoiceStatus
  weekNr: string
  weekLabel: string
  type: WerkbonType
  // Batch-3 D — bedragen + uren op het overzicht: excl. btw, btw-bedrag,
  // incl. btw (= total) en het urentotaal.
  subtotal: number
  vatAmount: number
  total: number
  totaalUren: number
  // Batch-3 D — betaalstatus op het overzicht (Betaald/Openstaand-indicatie).
  betaaldOp: string | null
  createdAt: string
  // FO-039 (batch-16) — de "Datum"-kolom = factuurdatum (invoiceDate), niet de
  // aanmaakdatum. `dueDate` = uiterste betaaldatum; `paymentTermDays` = de
  // betalingstermijn per BV (Entity.paymentTermDays, één bron); `vervallen` =
  // openstaand én vervaldatum verstreken (server-side computed in NL-tijd, geen
  // opgeslagen status).
  invoiceDate: string | null
  dueDate: string | null
  paymentTermDays: number | null
  vervallen: boolean
  client: { id: string; name: string; clientNumber: string | null } | null
  entity: { id: string; name: string } | null
  werkbonCount: number
  attachmentCount: number
  // PR-K (spec 09) — bron + credit + lock-indicator voor lijst-badges
  origin: InvoiceOrigin
  isCredit: boolean
  isLocked: boolean
  installmentNumber: number | null
  installmentTotal: number | null
  // Interne proforma-factuur voor Finqle-klanten (geen echt STREEP-factuurnummer;
  // UI toont "via Finqle - geen nummer" i.p.v. het FINQLE-<nr> sentinel).
  // batch-63 (FO-470): legacy - de gewone lijst toont ze niet meer; alleen het
  // verwijderd-filter (na de opschoning) kan er nog één bevatten.
  isFinqleProforma: boolean
  // FO-419 (batch-55) — factuur tussen eigen BV's: badge "Intercompany" +
  // filter; telt niet mee in de externe omzet-KPI's.
  isIntercompany: boolean
  intercompanyTegenpartij: { id: string; name: string } | null
  // batch-60 facturen-verwijderen (FO-443) - server-side bepaald; de UI doet
  // geen eigen statuscheck. `verwijdering` alleen gevuld in het verwijderd-filter.
  verwijderbaarheid: FactuurVerwijderbaarheidDTO
  verwijdering: FactuurVerwijderingDTO | null
  // batch-63 facturen-bronregel (FO-470) — nummer-stand (concept is expliciet
  // een concept, een rij zonder enig nummer is een zichtbare uitzondering) en het
  // aantal gekoppelde facturatieverzoeken (badge "Finqle-verzoek").
  nummerStand: FactuurNummerStand
  facturatieverzoekCount: number
}

export interface InvoiceListKpi {
  openstaandTotaal: number
  wachtOpKlantCount: number
  /** Globaal aantal AKKOORD_KLANT (klaar om definitief te maken). DB-breed,
   * niet beperkt tot de huidige pagina/statusfilter — zodat de facturatie-
   * workflow zijn "Klaar voor definitief"-badge ook bij paginering klopt. */
  akkoordKlantCount: number
  definitiefCount: number
  betaaldDezeMaandTotaal: number
  // FO-419 (batch-55) — intercompany apart: de externe KPI's hierboven sluiten
  // intercompany-facturen uit; dit zijn de intercompany-tegenhangers.
  /** Openstaand totaal van intercompany-facturen (zelfde statusset als `openstaandTotaal`). */
  intercompanyOpenstaandTotaal: number
  /** Deze maand betaald aan intercompany-facturen. */
  intercompanyBetaaldDezeMaandTotaal: number
  /** Aantal intercompany-facturen in de huidige gefilterde set. */
  intercompanyAantal: number
}

/** batch-9 fa.5 — per-BV samenvatting over de hele gefilterde dataset (boomstructuur). */
export interface InvoiceListByEntity {
  entityId: string
  name: string
  count: number
  totaal: number
  // FO-419 (batch-55) — waarvan intercompany (aantal + totaal), apart zichtbaar.
  intercompanyCount: number
  intercompanyTotaal: number
}

export interface InvoiceListMeta {
  total: number
  page: number
  limit: number
  kpi: InvoiceListKpi
  // batch-9 fa.5 — per-BV teller + totaal (voor de inklapbare boomstructuur).
  byEntity: InvoiceListByEntity[]
  // batch-63 facturen-bronregel (FO-470) — teller "n facturatieverzoeken zonder
  // ontvangen factuur" (server-side, volledige set) met link naar de werkvoorraad.
  verzoekenZonderFactuur: number
  // batch-63 (FO-470) — oude Finqle-rijen zonder echt factuurnummer (interne
  // proforma's en gespiegelde API-rijen) die nog op opschoning wachten. Ze staan
  // niet meer in de lijst; de melding voorkomt dat dat stil gebeurt.
  oudeFinqleRijen: number
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export interface InvoiceLineDetailDTO {
  id: string
  werkbonId: string | null
  werkbonNumber: string | null
  position: number
  category: InvoiceLineCategory
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface InvoiceAuditEntryDTO {
  id: string
  action: string
  fromStatus: InvoiceStatus | null
  toStatus: InvoiceStatus | null
  note: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  performer: { id: string; name: string | null; email: string | null }
}

export interface InvoiceWerkbonRefDTO {
  id: string
  werkbonNumber: string
  type: WerkbonType
  weekNr: string
  status: string
}

export interface InvoiceClientSnapshotDTO {
  name: string
  clientNumber: string | null
  kvkNumber: string | null
  vatNumber: string | null
  email: string | null
  invoiceEmail: string | null
  street: string | null
  houseNumber: string | null
  postalCode: string | null
  city: string | null
  country: string | null
  // batch-9 fa.3 — bevroren t.a.v.-contactpersoon (naam + functie), zodat de
  // adressering behouden blijft na DEFINITIEF. Null = geen t.a.v. gekozen.
  tavContactNaam?: string | null
  tavContactFunctie?: string | null
}

export interface InvoiceAttachmentDTO {
  id: string
  fileName: string
  fileSize: number
  mimeType: string
  sortOrder: number
  downloadUrl: string
  createdAt: string
}

export interface InvoiceDetailDTO {
  id: string
  invoiceNumber: string
  status: InvoiceStatus
  weekNr: string
  weekLabel: string
  type: WerkbonType
  subtotal: number
  vatAmount: number
  total: number
  vatPercentage: number
  // §4.2 — true: factuur met BTW verlegd (0% btw, "BTW verlegd" op de PDF i.p.v.
  // een btw-bedrag). Gespiegeld van Client.vatReversed bij aanmaak; bewerkbaar
  // op een CONCEPT/GEREED-factuur (Batch-3 D Deel 3).
  btwVerlegd: boolean
  totaalUren: number
  totaalReisKm: number
  totaalReisMinuten: number
  totaalPloegentoeslag: number
  packageType: PackageType | null
  shiftType: ShiftType | null
  afgekeurdReden: string | null
  verzondenNaarKlantOp: string | null
  akkoordOp: string | null
  definitiefOp: string | null
  verzondenNaarAdminOp: string | null
  betaaldOp: string | null
  // Batch-3 D — handmatig "betaald" markeren (geen bankkoppeling): wie + ref.
  // `betaaldDoor` is de rauwe userId; `betaaldDoorNaam` is server-side geresolved
  // (audit-actor-conventie: id -> name ?? email). `betaalreferentie` = bankref/opmerking.
  betaaldDoor: string | null
  betaaldDoorNaam: string | null
  betaalreferentie: string | null
  // Finqle/Zenvoices-verzenden (2026-06-04) — outbound verzendstatus per kanaal.
  pdfVerzondenOp: string | null
  pdfBestemming: 'FINQLE' | 'KLANT' | null
  zenvoicesXmlVerzondenOp: string | null
  verzondenById: string | null
  // FO-129 (batch-26) — eigen "verzonden naar Finqle"-marker, los van de
  // klant-verzending (pdfVerzondenOp). Voedt de "Verstuur naar Finqle"-knop.
  verzondenNaarFinqleOp: string | null
  verzondenNaarFinqleEmailId: string | null
  // FO-132 (batch-27) — true wanneer er een opgeslagen Finqle-retour-XML is
  // (`finqleXmlPath`), de bron voor het (opnieuw) leveren van de UBL-XML aan
  // Zenvoices op de HYBRIDE/FINQLE-route. Stuurt de "Opnieuw naar Zenvoices"-knop.
  heeftFinqleXml: boolean
  /** PR-I (spec 06) — PO/inkoopordernummer, optioneel. */
  poNumber: string | null
  // batch-9 fa.3 — t.a.v.-contactpersoon (per factuur gekozen uit de klant-
  // contacten, alleen wijzigbaar zolang CONCEPT). `tavContactId` = de selectie;
  // `tavContactNaam` = de getoonde naam (snapshot na DEFINITIEF, anders live);
  // `clientContacts` = de keuzelijst voor de picker (alleen server-side gevuld).
  tavContactId: string | null
  tavContactNaam: string | null
  clientContacts: Array<{ id: string; naam: string; functie: string | null }>
  pdfPath: string | null
  createdAt: string
  updatedAt: string
  // PR-K (spec 09) — bron + credit + lock
  origin: InvoiceOrigin
  isCredit: boolean
  creditType: CreditType | null
  creditReason: string | null
  // Interne proforma-factuur voor Finqle-klanten (geen echt STREEP-factuurnummer;
  // admin/rapportage, niet verzendbaar/actionabel). UI toont "via Finqle - geen nummer".
  isFinqleProforma: boolean
  // FO-419 (batch-55) — intercompany: richting (afzender = `entity`, ontvanger =
  // `intercompanyTegenpartij`), herkomst van de markering en de doorklik naar het
  // patroon/voorstel in de Intercompany-module.
  isIntercompany: boolean
  intercompanyTegenpartij: { id: string; name: string } | null
  intercompanyBron: IntercompanyFactuurBron | null
  intercompanyPatroonId: string | null
  intercompanyPatroonStatus: IntercompanyPatroonStatus | null
  // batch-66 interne-geldstroom (FO-494) - de interne geldstroom waar deze
  // factuur de omzetkant (spiegel) van is, met de wederzijdse verwijzing naar de
  // inkoopfactuur; null = niet gekoppeld. Optioneel: alleen de detail-GET vult hem.
  interneGeldstroom?: InterneGeldstroomRefDTO | null
  /** Geregistreerde spiegel van een extern gemaakte interne factuur: buiten de factuurreeks. */
  buitenFactuurreeks?: boolean
  quoteId: string | null
  installmentNumber: number | null
  installmentTotal: number | null
  invoiceDate: string | null
  dueDate: string | null
  // FO-349 (batch-40) — de betalingstermijn zoals bevroren bij DEFINITIEF
  // (`snapshotEntityJson.paymentTermDays`), null zolang er geen snapshot is. De
  // factuurdatum-correctie rekent hiermee, niet met de live BV-termijn: de
  // bevroren PDF doet dat ook, dus anders toont de modal een andere vervaldatum
  // dan er op het document komt.
  bevrorenBetalingstermijnDagen: number | null
  notes: string | null
  referenceNumber: string | null
  exportedAt: string | null
  exportBatchId: string | null
  isLocked: boolean
  correctedAt: string | null
  correctedBy: string | null
  creditLinks: CreditInvoiceLinkDTO[]
  creditedByLinks: CreditInvoiceLinkDTO[]
  // batch-63 facturen-bronregel (FO-470) — facturatieverzoeken waaraan deze
  // factuur gekoppeld is (koppelaar), en de markering "zonder facturatieverzoek"
  // voor een Finqle-factuur die bij geen verzoek hoort.
  nummerStand: FactuurNummerStand
  facturatieverzoeken: Array<{
    id: string
    finqleId: string | null
    status: string
    statusLabel: string
    bron: FinqleVerzoekBronKey
  }>
  zonderFacturatieverzoek: boolean
  /** De koppelaar vond meerdere passende verzoeken: koppel handmatig. */
  facturatieverzoekMeerduidig: boolean
  // Per-BV-facturatie (2026-08): de effectieve route van DEZE factuur (klant ×
  // facturerende BV, incl. systeem-default), server-side geresolved. De UI
  // gebruikt dit voor de verzendknop-keuze i.p.v. `client.facturatieVia` zelf
  // na te bouwen. Null alleen wanneer de factuur geen klant (meer) heeft.
  facturatieMethodeEffectief: FacturatieViaKey | null
  client: {
    id: string
    name: string
    clientNumber: string | null
    email: string | null
    // Finqle/Zenvoices-verzenden (2026-06-04) — facturatie-route (null → FINQLE).
    // FO-406 (batch-51) - de union komt uit de gedeelde registry (FACTURATIE_VIA_WAARDEN).
  facturatieVia: FacturatieViaKey | null
  } | null
  entity: {
    id: string
    name: string
    kvkNumber: string | null
    btwNumber: string | null
    iban: string | null
    street: string | null
    houseNumber: string | null
    postalCode: string | null
    city: string | null
    paymentTermDays: number | null
  } | null
  createdBy: { id: string; name: string | null; email: string } | null
  snapshotClientJson: InvoiceClientSnapshotDTO | null
  /** BLOK 3 ronde 2 — gestructureerd overzicht per medewerker (mag null zijn voor oude facturen) */
  employeeSummaryJson: EmployeeSummaryDTO | null
  lines: InvoiceLineDetailDTO[]
  audits: InvoiceAuditEntryDTO[]
  werkbonnen: InvoiceWerkbonRefDTO[]
  attachments: InvoiceAttachmentDTO[]
  // batch-60 facturen-verwijderen (FO-443) - server-side bepaald (resolver).
  verwijderbaarheid: FactuurVerwijderbaarheidDTO
}

// ─── FO-300 (batch-26) — samenstelling van de factuur-PDF ("PDF-inhoud") ───────

/** Eén gekoppelde werkbon in het PDF-inhoud-overzicht. */
export interface InvoicePdfInhoudWerkbonDTO {
  id: string
  werkbonNumber: string
  weekNr: string
  type: WerkbonType
  /** FE-07 — besloten/intern/niet-facturabel: mag niet klantgericht mee. */
  geblokkeerd: boolean
  blokkadeReden: string | null
  /** Klantaftekening of klantportaal-akkoord aanwezig. */
  heeftAkkoord: boolean
}

/**
 * Wat de samengestelde factuur-PDF gaat bevatten + de waarschuwingen/blokkades.
 * GET /invoices/:id/pdf-inhoud. Voedt het UI-blok "PDF-inhoud" + de definitief-
 * maken-preview.
 */
export interface InvoicePdfInhoudDTO {
  factuurregels: number
  werkbonnenAantal: number
  klantAkkoordenAantal: number
  handmatigeBijlagen: number
  werkbonnen: InvoicePdfInhoudWerkbonDTO[]
  /** Niet-blokkerend (bv. geen klantakkoord). */
  waarschuwingen: string[]
  /** Hard blokkerend voor definitief maken/verzenden (FE-07). */
  blokkades: string[]
  kanDefinitief: boolean
  isCredit: boolean
  /** Bij een creditnota: of de onderbouwing van de bronfactuur is meegenomen. */
  creditMeesturen: boolean
}

// ─── Create-context: te factureren werkbonnen ─────────────────────────────────

export interface FacturatieWerkbonRefDTO {
  id: string
  werkbonNumber: string
  goedgekeurdOp: string | null
  totaalUren: number
  totaalOveruren: number
  totaalReisKm: number
  totaalReisMinuten: number
  totaalReisVergoeding: number
  uurtarief: number
  overuurtarief: number
  materiaalKosten: number
  total: number
}

export interface FacturatieGroupDTO {
  groupKey: string
  clientId: string
  clientName: string
  clientNumber: string | null
  /** PR-I (spec 06) — klant vereist PO-nummer op de factuur. */
  clientRequirePONumber: boolean
  entityId: string | null
  entityName: string | null
  weekNr: string
  weekLabel: string
  type: WerkbonType
  werkbonCount: number
  /** BLOK 3 ronde 2 — aantal unieke medewerkers in de groep */
  employeeCount: number
  /** BLOK 3 ronde 2 — namen van betrokken medewerkers (alfabetisch) */
  employeeNames: string[]
  totaalUren: number
  totaalOveruren: number
  totaalReisKm: number
  totaalReisMinuten: number
  arbeidsKosten: number
  overurenKosten: number
  reisKosten: number
  materiaalKosten: number
  ploegentoeslag: number
  subtotal: number
  vatAmount: number
  total: number
  shiftType: ShiftType | null
  packageType: PackageType | null
  /**
   * batch-68 controle (FO-343 §4.4) - de werkbonnummers die onder een vaste prijs per
   * periode vallen. Zo'n groep gaat via het facturatie-overzicht (één vaste prijs per
   * periode); de directe factuurknop weigert hem. Leeg = gewoon factureerbaar.
   */
  vastePrijsViaOverzicht?: string[]
  /**
   * batch-68 controle (review fase B) - de werkbonnen waarvan een werkdag nog niet
   * compleet is: voor dezelfde medewerker staat op die dag bij deze klant nog een
   * dagregel die niet definitief is, terwijl er een afspraak per dag geldt (minimum,
   * maximum, afronding per dag, reistijd per dag). Per werkbon de dagregels waarop hij
   * wacht. Die bon factureer je zodra de dag compleet is; de rest van de groep kan wel.
   */
  dagNietCompleet?: DagNietCompleetDTO[]
  werkbonnen: FacturatieWerkbonRefDTO[]
}

/**
 * batch-68 controle - waarom een dagregel de werkdag nog openhoudt. Definitief is een
 * dagregel pas als hij is afgesloten EN zijn werkbon is goedgekeurd; tot dan kan hij
 * nog veranderen (een klant die afkeurt zet de dagregels terug op actief).
 */
export type DagOpenReden =
  | 'LOPEND'
  | 'HEROPEND'
  | 'WACHT_OP_INTERN'
  | 'WACHT_OP_KLANT'
  | 'AFGEKEURD'
  // batch-69 (FO-511): intern goedgekeurd, de klantstap wacht op de pauzeregel van de klant.
  | 'WACHT_OP_PAUZEREGEL'

export const DAG_OPEN_REDEN_LABEL: Record<DagOpenReden, string> = {
  LOPEND: 'nog niet gereedgemeld',
  HEROPEND: 'heropend voor correctie',
  WACHT_OP_INTERN: 'wacht op interne goedkeuring',
  WACHT_OP_KLANT: 'wacht op akkoord van de klant',
  AFGEKEURD: 'afgekeurd, wordt gecorrigeerd',
  WACHT_OP_PAUZEREGEL: 'wacht op de pauzeregel van de klant (gaat daarna naar de klant voor akkoord)',
}

/**
 * Een dagregel die de werkdag van een te factureren werkbon nog openhoudt. Van een
 * besloten werkbon (FO-008) alleen het neutrale blok: `werkbonNumber` "Besloten",
 * zonder `werkbonId` (geen link) en zonder medewerker.
 */
export interface OpenDagregelDTO {
  werkbonId: string | null
  werkbonNumber: string
  /** De kalenderdag (yyyy-MM-dd). */
  datum: string
  medewerker: string | null
  reden: DagOpenReden
}

export interface DagNietCompleetDTO {
  werkbonNumber: string
  open: OpenDagregelDTO[]
}

export interface FacturatieClientOption {
  id: string
  name: string
  clientNumber: string | null
}

export interface FacturatieEntityOption {
  id: string
  name: string
}

export interface FacturatieCreateContext {
  groups: FacturatieGroupDTO[]
  clients: FacturatieClientOption[]
  entities: FacturatieEntityOption[]
  /**
   * De werkbonnen-tab toont de volledige backlog van goedgekeurde-maar-nog-
   * niet-gefactureerde werkbonnen (geen periode-bound). De API capt het
   * bron-resultaat op `werkbonnenCap`; `werkbonnenCapped` is true zodra die cap
   * geraakt is zodat de UI een "verfijn met filters"-notitie kan tonen.
   */
  werkbonnenCapped?: boolean
  werkbonnenCap?: number
}

// ─── FO-032 (batch-16) — werkbon uit/in facturatie halen ─────────────────────
//
// "Uit facturatie halen" = Werkbon.facturabel=false + een reden (auditbaar,
// reversibel, soft; nooit hard delete). De waarden spiegelen de Prisma-enum
// WerkbonNietFacturabelReden. Eén gedeeld schema: de API parset ermee én het
// form valideert er client-side mee (geen kale Validatiefout-toast).

// batch-63 werkbon-facturatie (FO-474) — de redenen (+ systeemreden
// KLANT_INSTELLING, handmatige subset, schrijfregel en labels) leven in een
// blad-module om een runtime-importcyclus met werkbonnen.ts te voorkomen; ze
// komen via deze her-export gewoon in de barrel.
export * from './werkbon-facturabel-reden'

/**
 * Toggle van de facturabel-vlag vanuit de klaar-voor-facturatie-lijst. Bij
 * `facturabel=false` is een reden verplicht (audit-trail). Bij `facturabel=true`
 * (terughalen) hoort er geen reden mee te gaan.
 *
 * batch-63 (FO-474): bij OVERIG is ook de toelichting verplicht (vrije tekst
 * waar de vaste lijst tekortschiet, maar nooit leeg), en de systeemreden
 * KLANT_INSTELLING is hier niet kiesbaar.
 */
export const WerkbonFacturabelToggleSchema = z
  .object({
    facturabel: z.boolean(),
    reden: z.enum(WERKBON_NIET_FACTURABEL_REDEN_HANDMATIG).nullable().optional(),
    toelichting: z.string().trim().max(FACTURABEL_TOELICHTING_MAX).optional(),
  })
  .refine((d) => d.facturabel || !!d.reden, {
    message: 'Een reden is verplicht om een werkbon uit de facturatie te halen.',
    path: ['reden'],
  })
  .refine(
    (d) =>
      d.facturabel ||
      d.reden !== 'OVERIG' ||
      (d.toelichting ?? '').trim().length >= FACTURABEL_TOELICHTING_MIN,
    {
      message: `Licht "Overig" toe (minimaal ${FACTURABEL_TOELICHTING_MIN} tekens).`,
      path: ['toelichting'],
    },
  )
export type WerkbonFacturabelToggleInput = z.infer<typeof WerkbonFacturabelToggleSchema>

/** Eén rij in de "Niet te factureren"-weergave (uitgesloten werkbonnen). */
export interface NietFacturabelWerkbonDTO {
  id: string
  werkbonNumber: string
  clientName: string
  weekNr: string
  weekLabel: string
  type: WerkbonType
  reden: WerkbonNietFacturabelReden | null
  /** Wanneer de werkbon uit de facturatie is gehaald (laatste audit). */
  uitFacturatieOp: string | null
}

// ─── EmployeeSummary (BLOK 3 ronde 2 — facturatie) ───────────────────────────

export interface EmployeeSummaryEntryDTO {
  naam: string
  employeeId: string | null
  werkbonNumbers: string[]
  totaalUren: number
  totaalOveruren: number
  totaalReisKm: number
  totaalReisMinuten: number
  arbeidsKosten: number
  overurenKosten: number
  reisKosten: number
  reistijdKosten: number
  materiaalKosten: number
  subtotaal: number
}

export interface EmployeeSummaryDTO {
  employees: EmployeeSummaryEntryDTO[]
  totaalPloegentoeslag: number
  totaalMateriaal: number
}

/** Klant-veilige variant — geen unitPrice/uurtarief, identieke shape verder. */
export type ClientEmployeeSummaryDTO = EmployeeSummaryDTO

// ─── Generate-all (dry-run) preview ──────────────────────────────────────────

export interface GenerateAllPreviewItem {
  clientId: string
  clientName: string
  weekLabel: string
  weekNr: string
  type: WerkbonType
  entityId: string | null
  entityName: string | null
  werkbonCount: number
  employeeCount: number
  subtotal: number
  total: number
  werkbonNumbers: string[]
}

export interface GenerateAllPreviewError {
  groupKey: string
  clientId: string
  clientName: string
  weekNr: string
  type: WerkbonType
  werkbonNumbers: string[]
  reden: string
  details?: { fouten?: string[]; waarschuwingen?: string[] }
}

export interface GenerateAllPreviewResponse {
  preview: GenerateAllPreviewItem[]
  totaalFacturen: number
  totaalBedrag: number
  fouten: GenerateAllPreviewError[]
}

// ─── Number sequence (BLOK 3 ronde 2) ────────────────────────────────────────

export interface NumberSequenceRowDTO {
  id: string
  entityId: string
  entityName: string
  bvCode: string
  year: number
  lastNumber: number
  startAt: number
  /** Voorbeeld van het volgende factuurnummer */
  nextNumberPreview: string
}

// ─── PR-K (spec 09) — Nieuwe DTOs voor 4 bronnen + credits + import ──────────

/** Een handmatige factuurregel (POST /invoices/handmatig + manual credit). */
export interface ManualInvoiceLineInput {
  category: InvoiceLineCategory
  description: string
  clientDescription?: string
  quantity: number
  unitPrice: number
}

/** Body voor POST /invoices/handmatig (§4.2). */
export interface CreateManualInvoiceInput {
  clientId: string
  entityId: string
  type?: WerkbonType
  vatPercentage?: number
  invoiceDate?: string
  dueDate?: string
  weekNr?: string
  weekLabel?: string
  notes?: string
  /** PR-I (spec 06) — ordernummer/PO-nummer van de klant; verplicht als Client.requirePONumber. */
  poNumber?: string
  lines: ManualInvoiceLineInput[]
  /**
   * FO-417 — "alsnog toevoegen": vul een geregistreerd reeks-gat. De factuur
   * krijgt dan direct exact dat nummer; de BV van het gat moet `entityId` zijn.
   */
  factuurreeksGatId?: string
}

/** Termijn-regel voor offerte-factuur (§3.3). */
export interface QuoteInstallmentInput {
  percentage: number
  omschrijving?: string
}

/** Body voor POST /invoices/from-quote (§3.3). */
export type CreateInvoiceFromQuoteInput =
  | { mode: 'VOLLEDIG'; quoteId: string }
  | { mode: 'TERMIJNEN'; quoteId: string; termijnen: QuoteInstallmentInput[] }

/** Body voor POST /invoices/:id/credit (§6.3). */
export interface CreateCreditInvoiceInput {
  creditType: CreditType
  reason: string
  lines?: ManualInvoiceLineInput[]
  // FO-300 (batch-26) — neem de onderliggende werkbonnen + klantakkoord van de
  // bronfactuur opnieuw mee in de creditnota-PDF (default Ja). Bij discussie over
  // een correctie wil je dezelfde onderbouwing kunnen meesturen.
  meesturenWerkbonnen?: boolean
}

/** Body voor POST /invoices/credit/manual (§6.4). */
export interface CreateManualCreditInput {
  clientId: string
  // batch-9 fa.6 — BV voor een LOSSE creditnota zonder factuurkoppeling. Bij wél
  // gekoppelde facturen wordt de BV uit die facturen afgeleid (entityId genegeerd).
  entityId?: string
  reason: string
  vatPercentage?: number
  lines: ManualInvoiceLineInput[]
  // batch-9 fa.6 — koppeling is optioneel (lege array = losse creditnota).
  linkedInvoices: Array<{ invoiceId: string; amount: number; note?: string }>
}

/** Credit-link in de UI / detail. */
export interface CreditInvoiceLinkDTO {
  id: string
  amount: number
  note: string | null
  createdAt: string
  creditInvoice?: { id: string; invoiceNumber: string; total: number; status: InvoiceStatus }
  originalInvoice?: { id: string; invoiceNumber: string; total: number; status: InvoiceStatus }
}

/** GET /invoices/credit-status — credit-overzicht. */
export interface CreditStatusItemDTO {
  invoiceId: string
  invoiceNumber: string
  originalTotal: number
  creditedTotal: number
  netTotal: number
  creditCount: number
  isFullyCredited: boolean
}

/** Import-wizard: één geanalyseerde regel met match-suggesties (§7.2 + §7.6). */
export interface ImportInvoiceRow {
  rowKey: string
  source: 'PDF' | 'EXCEL'
  sourceFile?: string
  factuurnummer: string | null
  debiteurnaam: string | null
  debiteurKvk: string | null
  bvNaam: string | null
  datum: string | null
  vervaldatum: string | null
  subtotaal: number | null
  btwBedrag: number | null
  btwPercentage: number | null
  totaal: number | null
  referentienummer: string | null
  omschrijving: string | null
  matched: {
    clientId: string | null
    clientName: string | null
    clientConfidence: 'high' | 'medium' | 'low' | 'none'
    clientReasoning: string
    entityId: string | null
    entityName: string | null
    entityConfidence: 'high' | 'medium' | 'low' | 'none'
    entityReasoning: string
  }
  errors: string[]
  warnings: string[]
}

export interface ImportInvoiceAnalyzeResponse {
  rows: ImportInvoiceRow[]
  aiEnabled: boolean
  warning?: string
}

export interface ImportInvoiceCommitInput {
  rows: Array<{
    rowKey: string
    clientId: string
    entityId: string
    factuurnummer: string
    datum: string
    vervaldatum?: string | null
    subtotaal: number
    btwBedrag: number
    btwPercentage: number
    omschrijving: string | null
    referentienummer: string | null
    sourceFile: string | null
  }>
}

// ─── FO-349 (batch-40) — factuurdatum handmatig instelbaar ────────────────────
//
// Eén gedeeld schema voor de drie schrijfpaden (definitief-modal, concept-PATCH,
// correctie-route): de API parset ermee én het form valideert er client-side mee.
// Alleen EIGEN verkoopfacturen; inkoop (Finqle) heeft zijn eigen datumbron (A1).
//
// Let op de read↔write-consistentie: de read-DTO geeft `invoiceDate` terug als
// ISO-datetime (`Invoice.invoiceDate` is een DateTime), terwijl het form een
// kale NL-kalenderdag ('yyyy-MM-dd') stuurt. Bèide vormen moeten geaccepteerd
// worden, anders weigert het write-schema zijn eigen read-DTO (CLAUDE.md
// "Validatie & foutmeldingen"). De API normaliseert daarna naar het NL-dagbegin.

/** Vangnet-bereik voor een handmatig gekozen factuurdatum: vandaag ± 1 jaar (A4). */
export const FACTUURDATUM_JAAR_MARGE = 1

const FACTUURDATUM_YMD_RE = /^\d{4}-\d{2}-\d{2}$/

/** Is dit een kale NL-kalenderdag ('yyyy-MM-dd')? */
export function isFactuurdatumYmd(waarde: string): boolean {
  if (!FACTUURDATUM_YMD_RE.test(waarde)) return false
  // Wijst de string een bestaande dag aan? (Vangt 2026-02-31 / 2026-13-01.)
  const [j, m, d] = waarde.split('-').map((x) => parseInt(x, 10))
  const t = new Date(Date.UTC(j, m - 1, d))
  return (
    t.getUTCFullYear() === j && t.getUTCMonth() === m - 1 && t.getUTCDate() === d
  )
}

/** Volledige ISO-datetime, de vorm die de read-DTO teruggeeft. */
const FACTUURDATUM_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/**
 * Accepteert een NL-kalenderdag ('yyyy-MM-dd') OF een volledige ISO-datetime
 * (de vorm die de read-DTO teruggeeft). De API zet beide om naar de NL-dag en
 * slaat op als NL-dagbegin; zie `factuurdatumNaarNlDag` in `@streep/utils`.
 *
 * Bewust NIET "alles wat `Date.parse` slikt": dat accepteert ook '2026' (→ 1
 * januari) en 'juli 2026' (→ 1 juli). Met handmatige invoer op de DatePicker
 * zou een vertypte datum dan stilletjes een verkeerde dag worden in plaats van
 * een veldfout.
 */
export const FactuurdatumInvoerSchema = z
  .string()
  .trim()
  .min(1, 'Kies een factuurdatum')
  .refine(
    (v) =>
      isFactuurdatumYmd(v) || (FACTUURDATUM_ISO_RE.test(v) && !Number.isNaN(Date.parse(v))),
    'Ongeldige datum - gebruik dd-mm-jjjj',
  )

/**
 * Variant voor de conceptfactuur, waar de datum ook gewíst mag worden ("wordt
 * vandaag bij uitgifte", §3.2). Een leeg tekstveld stuurt `''`; dat is hier de
 * lege waarde, niet een validatiefout.
 */
export const FactuurdatumWisbaarSchema = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
  FactuurdatumInvoerSchema.nullable(),
)

/**
 * Ligt `ymd` verder dan `marge` jaar van `vandaagYmd`? Puur kalender-rekenwerk
 * (geen tijdzone): de aanroeper levert beide dagen al als NL-kalenderdag.
 *
 * Bewust ruim - dit is een typefout-vangnet ("2025" i.p.v. "2026"), geen
 * boekhoudregel. STREEP kent geen periode-afsluiting in de app (A2), dus
 * antedateren binnen het bereik is een waarschuwing, geen blokkade.
 */
export function factuurdatumBuitenBereik(
  ymd: string,
  vandaagYmd: string,
  marge: number = FACTUURDATUM_JAAR_MARGE,
): boolean {
  if (!isFactuurdatumYmd(ymd) || !isFactuurdatumYmd(vandaagYmd)) return false
  const [j, m, d] = vandaagYmd.split('-').map((x) => parseInt(x, 10))
  // Via Date.UTC zodat 29 februari + 1 jaar netjes naar 1 maart schuift i.p.v.
  // een niet-bestaande dag op te leveren.
  const grens = (richting: number): string => {
    const t = new Date(Date.UTC(j + richting * marge, m - 1, d))
    const p2 = (n: number) => String(n).padStart(2, '0')
    return `${t.getUTCFullYear()}-${p2(t.getUTCMonth() + 1)}-${p2(t.getUTCDate())}`
  }
  // 'yyyy-MM-dd' is lexicografisch = chronologisch.
  return ymd < grens(-1) || ymd > grens(1)
}

export const FACTUURDATUM_BUITEN_BEREIK_MELDING =
  'Factuurdatum meer dan een jaar van vandaag - controleer de invoer'

/** Body van `PATCH /invoices/:id/status` met `action: 'maak-definitief'`. */
export const MaakDefinitiefFactuurdatumSchema = z.object({
  /**
   * Weglaten = het bestaande gedrag: de factuurdatum wordt het uitgiftemoment
   * (`now`, gelijk aan `definitiefOp`). Meegeven = de gekozen NL-kalenderdag,
   * opgeslagen als NL-dagbegin.
   */
  invoiceDate: FactuurdatumInvoerSchema.optional(),
})

/** Body van `POST /invoices/:id/corrigeer-datum` (ná DEFINITIEF, vóór verzending). */
export const CorrigeerFactuurdatumSchema = z.object({
  invoiceDate: FactuurdatumInvoerSchema,
})
export type CorrigeerFactuurdatumInput = z.infer<typeof CorrigeerFactuurdatumSchema>

/**
 * Informatieve waarschuwingen bij een gekozen factuurdatum. Geen blokkades:
 * ze verschijnen inline in de modal zodat de keuze bewust is (§7).
 */
export type FactuurdatumWaarschuwingCode =
  /** Datum ligt vóór vandaag - de factuur valt in een eerdere periode. */
  | 'DATUM_IN_VERLEDEN'
  /** Datum ligt ná vandaag - openstaand-bewaking start pas vanaf die datum. */
  | 'DATUM_IN_TOEKOMST'
  /** Eerdere datum dan het vorige factuurnummer van dezelfde BV (§8.3). */
  | 'DATUM_INVERSIE_VORIG_NUMMER'
  /** Vervaldatum is ooit handmatig gezet en volgt de factuurdatum dus niet (§3.5). */
  | 'VERVALDATUM_HANDMATIG'

export interface FactuurdatumWaarschuwing {
  code: FactuurdatumWaarschuwingCode
  /** Kant-en-klare NL-tekst; de UI toont 'm ongewijzigd. */
  message: string
}

/**
 * Succes-response van de definitief- en correctie-paden. `warnings` is altijd
 * aanwezig (lege array = niets aan de hand) zodat de UI niet hoeft te raden.
 */
export interface FactuurdatumMutatieResultDTO {
  id: string
  invoiceDate: string | null
  dueDate: string | null
  warnings: FactuurdatumWaarschuwing[]
}

// ─── FO-415 — factuurnummer-teller volgt geïmporteerde nummers ────────────────
//
// Een geïmporteerde verkoopfactuur (wizard single/bulk, mail-import) behoudt haar
// originele nummer. Past dat nummer in het eigen reeksformaat van de BV
// (`FA{JJJJ}{bvCode}{volgnr}`), dan schuift de teller van die BV + dat jaar mee
// (alleen omhoog, nooit omlaag). Elk importpad geeft dit resultaat terug zodat
// de UI het effect toont in plaats van het stil te laten gebeuren.

export type NummerreeksImportStatus =
  /** Nummer past in de reeks en lag boven de teller: de teller staat nu op dit nummer. */
  | 'BIJGEWERKT'
  /** Nummer past in de reeks maar lag niet boven de teller: niets veranderd. */
  | 'ONGEWIJZIGD'
  /** Nummer heeft niet het FA-reeksformaat (bv. uit het oude systeem): telt niet mee. */
  | 'AFWIJKEND_FORMAAT'
  /** FA-formaat, maar de BV-code in het nummer is niet die van deze BV: telt niet mee. */
  | 'ANDERE_BV_CODE'
  /** De BV heeft geen factuurnummer-code en dus geen eigen reeks om bij te werken. */
  | 'GEEN_REEKS'
  /**
   * batch-63 (FO-471 O4) — FA-formaat en eigen BV-code, meer dan
   * `NUMMERREEKS_MAX_SPRONG` maar hoogstens `NUMMERREEKS_SPRONG_PLAFOND` boven de
   * teller. De teller is WEL opgehoogd (anders trekt de volgende eigen factuur
   * een bestaand nummer); het tussenliggende bereik staat als gaten met reden
   * "sprong door import" in de reeksbewaking, in één keer te accepteren.
   */
  | 'SPRONG_OVERGENOMEN'
  /**
   * FA-formaat en eigen BV-code, maar meer dan `NUMMERREEKS_SPRONG_PLAFOND`
   * boven de teller: vrijwel zeker een typo of AI-misread. Niet overgenomen (de
   * teller kent geen weg terug) en als afwijking zichtbaar in de reeksbewaking.
   * Klopt het nummer wél, dan zet de beheerder het startpunt handmatig.
   */
  | 'SPRONG_TE_GROOT'

/**
 * Tot deze sprong (t.o.v. de huidige stand, of het ingestelde startpunt als dat
 * hoger is) werkt een import de teller gewoon bij. Daarboven wordt de sprong
 * zichtbaar als afwijking (FO-471 O4).
 */
export const NUMMERREEKS_MAX_SPRONG = 1000

/**
 * batch-63 (FO-471 O4) — vangnet boven de gewone sprong: één verkeerd gelezen
 * nummer mag de fiscale teller niet onherstelbaar wegschieten. Een sprong
 * groter dan dit wordt niet overgenomen maar als afwijking gemeld.
 */
export const NUMMERREEKS_SPRONG_PLAFOND = 10000

/** Max. aantal voorbeeld-nummers per niet-meegetelde groep in de bulk-samenvatting. */
export const NUMMERREEKS_VOORBEELDEN_MAX = 5

/** Statussen waarbij het nummer NIET in de reeks is meegeteld (zichtbaar melden). */
export type NummerreeksNietMeegeteldStatus = Exclude<
  NummerreeksImportStatus,
  'BIJGEWERKT' | 'ONGEWIJZIGD' | 'SPRONG_OVERGENOMEN'
>

export interface NummerreeksImportResultaat {
  status: NummerreeksImportStatus
  factuurnummer: string
  entityId: string
  entityName: string
  /** Jaar van de reeks (uit het nummer); null als het nummer niet in de reeks past. */
  year: number | null
  /** Volgnummer uit het nummer; null als het nummer niet in de reeks past. */
  volgnummer: number | null
  /** Stand van de teller ná deze import; null als het nummer niet in de reeks past. */
  lastNumber: number | null
  /** Nummer dat de eerstvolgende app-factuur in deze reeks krijgt; null als niet van toepassing. */
  volgendNummer: string | null
}

export const NUMMERREEKS_IMPORT_STATUS_LABELS: Record<NummerreeksImportStatus, string> = {
  BIJGEWERKT: 'Teller bijgewerkt',
  ONGEWIJZIGD: 'Nummer niet hoger dan de teller - ongewijzigd',
  AFWIJKEND_FORMAAT: 'Afwijkend nummerformaat - telt niet mee in de reeks',
  ANDERE_BV_CODE: 'BV-code in het nummer hoort niet bij deze BV - telt niet mee in de reeks',
  GEEN_REEKS: 'BV heeft geen factuurnummer-code - geen reeks om bij te werken',
  SPRONG_OVERGENOMEN: `Sprong van meer dan ${NUMMERREEKS_MAX_SPRONG} - teller opgehoogd, tussenliggende nummers als gaten geregistreerd`,
  SPRONG_TE_GROOT: `Nummer ligt meer dan ${NUMMERREEKS_SPRONG_PLAFOND} boven de teller - niet overgenomen (controleer het nummer of zet het startpunt handmatig)`,
}

/** Eén leesbare zin over het reeks-effect van één import (toast / succeskaart). */
export function beschrijfNummerreeksResultaat(r: NummerreeksImportResultaat): string {
  switch (r.status) {
    case 'BIJGEWERKT':
      return `Factuurnummer-teller van ${r.entityName} (${r.year}) bijgewerkt naar ${r.lastNumber}; de volgende app-factuur wordt ${r.volgendNummer}.`
    case 'ONGEWIJZIGD':
      return `Factuurnummer-teller van ${r.entityName} (${r.year}) blijft op ${r.lastNumber}; de volgende app-factuur wordt ${r.volgendNummer}.`
    case 'AFWIJKEND_FORMAAT':
      return `Nummer ${r.factuurnummer} heeft een afwijkend formaat en telt niet mee in de factuurreeks van ${r.entityName}; de teller is ongemoeid.`
    case 'ANDERE_BV_CODE':
      return `Nummer ${r.factuurnummer} draagt een andere BV-code en telt niet mee in de factuurreeks van ${r.entityName}; de teller is ongemoeid.`
    case 'GEEN_REEKS':
      return `${r.entityName} heeft geen factuurnummer-code; er is geen reeks om bij te werken.`
    case 'SPRONG_OVERGENOMEN':
      return `Nummer ${r.factuurnummer} sprong meer dan ${NUMMERREEKS_MAX_SPRONG} vooruit: de teller van ${r.entityName} (${r.year}) staat nu op ${r.lastNumber} en de tussenliggende nummers staan als gaten in de reeksbewaking (Facturen, tab Nummerreeks), in één keer te accepteren.`
    case 'SPRONG_TE_GROOT':
      return `Nummer ${r.factuurnummer} ligt meer dan ${NUMMERREEKS_SPRONG_PLAFOND} boven de teller van ${r.entityName} (${r.year}, nu ${r.lastNumber}) en is niet overgenomen; de afwijking staat in de reeksbewaking. Klopt het nummer, zet dan het startpunt handmatig via Instellingen → Facturatie.`
  }
}

/**
 * Samenvatting over een bulk-import (ImportJob-resultaat): per BV/jaar de
 * eindstand van de teller + per BV/status hoeveel nummers niet zijn meegeteld
 * (met enkele voorbeelden). Bewust geaggregeerd: een legacy-bulk van duizenden
 * facturen mag geen duizenden losse regels in `ImportJob.resultaat` of op de
 * job-detailpagina opleveren (CLAUDE.md "Lijsten, limits & stille afkap").
 */
export interface NummerreeksImportSamenvatting {
  bijgewerkt: Array<{
    entityId: string
    entityName: string
    year: number
    lastNumber: number
    volgendNummer: string
  }>
  nietMeegeteld: Array<{
    entityId: string
    entityName: string
    status: NummerreeksNietMeegeteldStatus
    aantal: number
    /** Eerste `NUMMERREEKS_VOORBEELDEN_MAX` factuurnummers van deze groep. */
    voorbeelden: string[]
  }>
}
